/**
 * Can be used on SheetJS files, but NOT Dependent on SheetJS!
 */
export default class SheetManager {
  /**
   *
   * @param {Array<Object>} sheetCells
   */
  constructor (sheetCells) {
    this.originalSheetCells = sheetCells

    this.sheetCells = this._copyCells(sheetCells)
    this._indexRowsAndColumns(this.sheetCells)

    // Would managing cells in a matrix be faster?
    // this.cellsMatrix = []
  }

  static sheetCellRefMeta (refA1) {
    sheetCellRefMeta(refA1)
  }

  /**
   * To then allow manipulation of the cells
   * The cells are copied, and metadata is added
   * @param {Array<Object>} sheetCells
   * @returns {Array<Object>}
   */
  _copyCells (sheetCells) {
    if (!sheetCells) return []
    if (!Array.isArray(sheetCells)) return []

    return sheetCells.map(function (cell) {
      const cellRefMeta = sheetCellRefMeta(cell.refA1)
      return { ...cell, ...cellRefMeta }
    })
  }

  /**
   * static method: can be used without instantiating the class
   * @param {String} cellRefA1
   * @returns {Array<Number>} [row, column] cell row and column numbers, in 0-indexed
   */
  static cellRefA1toRC (cellRefA1) {
    if (!cellRefA1) return null
    const [columnLetters, _rowNumber] = splitCellRef(cellRefA1)

    if (!_rowNumber) return null
    if (!_rowNumber.match(/^\d+$/)) return null
    if (!columnLetters) return null

    const rowNumber = parseInt(_rowNumber) - 1

    const columnNumbers = []
    columnLetters.split('').forEach(function (letter) {
      const columnNumber = letter.charCodeAt(0) - 64
      columnNumbers.push(columnNumber)
    })

    const columnNumber = columnNumbers.reduce((acc, curr) => acc * 26 + curr, 0) - 1

    return [rowNumber, columnNumber]
  }

  static cellRefA1Letter (cellRefA1) {
    if (!cellRefA1) return null
    const [columnLetters] = splitCellRef(cellRefA1)
    return columnLetters
  }

  _indexRowsAndColumns (sheetCells) {
    // console.log('_indexRowsAndColumns sheetCells', sheetCells.length, sheetCells)
    const instance = this

    instance.rows = {}
    instance.columns = {}

    sheetCells.forEach(function (cell) {
      // console.log('_indexRowsAndColumns cell', cell)

      // Index in rows
      instance.rows[cell.r] = instance.rows[cell.r] || {
        r: cell.r,
        rowNumber: cell.rowNumber,
        cells: []
      }
      instance.rows[cell.r].cells.push(cell)

      // Index in columns
      instance.columns[cell.c] = instance.columns[cell.c] || {
        c: cell.c,
        columnLetter: cell.columnLetter,
        cells: []
      }
      instance.columns[cell.c].cells.push(cell)
    })
    // console.log('_indexRowsAndColumns instance.rows', instance.rows)
    // console.log('_indexRowsAndColumns instance.columns', instance.columns)

    return this
  }

  get allRows () {
    return Object.values(this.rows)
  }

  get allColumns () {
    return Object.values(this.columns)
  }

  /**
   *
   * @param {String} refA1 A reference to a cell, in A1 format
   * @returns {Object} The cell object
   */
  getCell (refA1) {
    const [rowIndex, columnIndex] = SheetManager.cellRefA1toRC(refA1)
    console.log('getCell refA1', refA1, rowIndex, columnIndex)
    const cell = this.sheetCells.find(cell => cell.r === rowIndex && cell.c === columnIndex)
    console.log('getCell cell', cell)

    return cell
  }

  /**
   *
   * @param {Object} searchParams
   * @param {String} searchParams.value // The value of the cell to identify
   * @param {Array<Object>} searchParams.relatives
   * @param {String} searchParams.relatives.value // The value of the relative cell
   * @param {Number} searchParams.relatives.relativeRow
   * @param {Number} searchParams.relatives.relativeColumn
   * @returns {Object} The cell object
   */
  identifyCell (searchParams) {
    // console.log('identifyCell searchParams', searchParams)
    const { value, relatives } = searchParams

    // Find all potential cells with such value
    const potentialCells = this.sheetCells.filter(cell => cell.value === value)
    // console.log('identifyCell cells', potentialCells)

    let matchingCells = []

    if (relatives) {
      for (const potentialCell of potentialCells) {
        let matchingCell = true
        for (const relative of relatives) {
          const relativeRow = potentialCell.r + relative.relativeRow
          const relativeColumn = potentialCell.c + relative.relativeColumn
          const relativeCell = this.sheetCells.find(cell => cell.r === relativeRow && cell.c === relativeColumn)
          console.log('relativeCell', relativeRow, relativeColumn, relativeCell)
          if (!relativeCell) matchingCell = false
          if (relativeCell.value !== relative.value) matchingCell = false
        }
        if (matchingCell) {
          matchingCells.push(potentialCell)
        }
      }
      // console.log('identifyCell matchingCells with relatives', matchingCells)
    } else {
      matchingCells = potentialCells
    }
    // console.log('identifyCell matchingCells', matchingCells)

    return matchingCells[0] || null
  }

  /**
   * Fill cells of rows based on rows from above
   * @param {Object} fillParams
   * @param {String} fillParams.columnLetter
   * @param {Number} fillParams.relativeRow
   */
  fillCells (fillParams) {
    const { columnLetter, relativeRow } = fillParams

    for (const row of this.allRows) {
      const targetCell = this.sheetCells.find(cell => cell.columnLetter === columnLetter && cell.rowNumber === row.rowNumber)

      // Nothing to do it cell already defined
      if (targetCell) continue

      const relativeRowNumber = row.rowNumber + relativeRow
      const relativeCell = this.sheetCells.find(cell => cell.columnLetter === columnLetter && cell.rowNumber === relativeRowNumber)

      if (!relativeCell) continue

      const newCellRefA1 = `${columnLetter}${row.rowNumber}`
      const newCellRefMeta = sheetCellRefMeta(newCellRefA1)
      const newCell = {
        refA1: newCellRefA1,
        value: relativeCell.value,
        ...newCellRefMeta,
        autoFilled: true
      }
      this.sheetCells.push(newCell)
    }

    // Re-sort the cells
    this.sheetCells.sort((a, b) => { return a.r - b.r || a.c - b.c })

    // Re-index
    this._indexRowsAndColumns(this.sheetCells)
  }

  /**
   * To restrict cells, user filterCells() first
   * @param {*} settings
   */
  cellsToTable (settings) {
    // Rows to include?
    // Columns to include?
    // Headers?

    const cellsTable = []

    settings.cells.forEach(cell => {
      // console.log('cell', cell)
      const columnName = settings.columns.find(column => column.index === cell.c)?.name || cell.columnLetter

      cellsTable.push({
        columnName,
        ...cell
      })
    })

    return cellsTable
  }

  /**
   *
   * @param {Object} filters
   * @param {Array<Number>} filters.columns
   * @param {Number} filters.minRow
   * @param {Number} filters.minRowNumber
   * @param {Function} filters.rowCellsFilterFunction
   * ... Filters:
   * @param {String} filters.rowCellsFilterType 'OR'|'AND' (Default)
   * @param {Array<Object>} filters.rowCellsFilter
   * @param {Array<Object>} filters.rowCellsFilter.column
   * @param {Function} filters.rowCellsFilter.value
   * @returns {Array<Object>}
   */
  filterCells (filters) {
    const cells = this.sheetCells.filter(cell => {
      if (filters.columns && !filters.columns.includes(cell.c)) return false

      if (Number.isFinite(filters.minRow) && cell.r < filters.minRow) return false
      if (Number.isFinite(filters.minRowNumber) && cell.rowNumber < filters.minRowNumber) return false

      if (filters.rowCellsFilterFunction && !filters.rowCellsFilterFunction(this.rows[cell.r].cells)) return false

      if (!filters.rowCellsFilter) return true

      let numberOfFiltersPassed = 0

      for (const rowCellsFilter of filters.rowCellsFilter) {
        const rowCells = this.rows[cell.r].cells
        const targetCell = rowCells.find(rowCell => rowCell.c === rowCellsFilter.column)

        // if (!targetCell) continue
        const filterCellValue = rowCellsFilter.value(targetCell?.value)
        if (filterCellValue) numberOfFiltersPassed++
      }

      if (filters.rowCellsFilterType === 'OR') {
        return numberOfFiltersPassed > 0
      } else {
        return numberOfFiltersPassed === filters.rowCellsFilter.length
      }
    })

    return cells
  }
}

function sheetCellRefMeta (refA1) {
  const refRC = SheetManager.cellRefA1toRC(refA1)
  const columnLetter = SheetManager.cellRefA1Letter(refA1)
  return {
    r: refRC[0],
    c: refRC[1],

    columnLetter,
    rowNumber: refRC[0] + 1
  }
}

/**
 * Inspired by SheetJS's split_cell function
 * @param {String} cellRefA1
 * @returns {Array<String>}
 */
function splitCellRef (cellRefA1) {
  return cellRefA1.toUpperCase().replace(/(\$?[A-Z]*)(\$?\d*)/, '$1,$2').split(',')
}
